import type { Level } from '@outpost/api-client';

/** Compact relative time, e.g. `3m`, `2h`, `5d`. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

/** Absolute timestamp for tooltips, where precision matters. */
export function absoluteTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

/** Counts abbreviated with `k` and `m` once they stop being readable. */
export function compactNumber(value: number): string {
  if (value < 1000) return String(value);
  // Switch units where rounding would otherwise print `1000k`.
  const [scaled, unit] = value < 999_500 ? [value / 1000, 'k'] : [value / 1_000_000, 'm'];
  // One decimal while the whole part is a single digit, and no `.0`.
  const digits = scaled < 9.95 ? 1 : 0;
  return `${scaled.toFixed(digits).replace(/\.0$/, '')}${unit}`;
}

/** Tailwind text colour for a severity level. */
export function levelColor(level: Level): string {
  switch (level) {
    case 'fatal':
      return 'text-level-fatal';
    case 'error':
      return 'text-level-error';
    case 'warning':
      return 'text-level-warning';
    case 'info':
      return 'text-level-info';
    default:
      return 'text-level-debug';
  }
}

/** Background tint for a severity level, used for the issue-row rail. */
export function levelBackground(level: Level): string {
  switch (level) {
    case 'fatal':
      return 'bg-level-fatal';
    case 'error':
      return 'bg-level-error';
    case 'warning':
      return 'bg-level-warning';
    case 'info':
      return 'bg-level-info';
    default:
      return 'bg-level-debug';
  }
}

/**
 * Split an issue title into its exception type and message.
 *
 * The type is the part worth emphasising in a dense list; showing the whole
 * string at one weight makes a stream of issues hard to scan.
 */
export function splitTitle(title: string): { type: string | null; message: string } {
  const match = /^([A-Za-z_$][\w$.]*(?:Error|Exception|Warning)?):\s+([\s\S]+)$/.exec(title);
  if (match?.[1] && match[2] && /(Error|Exception|Warning)$/.test(match[1])) {
    return { type: match[1], message: match[2] };
  }
  return { type: null, message: title };
}

/** Shorten a file path to its last few segments, keeping the informative end. */
export function shortenPath(path: string, segments = 2): string {
  const clean = path.split('?')[0] ?? path;
  const parts = clean.split('/').filter(Boolean);
  if (parts.length <= segments) return clean;
  return `…/${parts.slice(-segments).join('/')}`;
}

/**
 * Up to two initials for an avatar, falling back to the address: an account
 * without a name still has to be recognisable in a row of circles.
 */
export function initials(name: string, email = ''): string {
  const source = name.trim() || (email.split('@')[0] ?? '');
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** A file size in the largest unit that keeps it readable, e.g. `1.2 MB`. */
export function formatBytes(bytes: number): string {
  if (!(bytes >= 1024)) return `${Math.max(0, Math.round(bytes) || 0)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  // Move up a unit where rounding would otherwise print `1024 KB`.
  while (value >= 1023.5 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal while the whole part is a single digit, and no `.0`.
  const digits = value < 9.95 ? 1 : 0;
  return `${value.toFixed(digits).replace(/\.0$/, '')} ${units[unit]}`;
}
